/**
 * Configuration for vehicle options
 * Each option defines:
 * - key: The API key used in vehicleOptions array
 * - formField: The form field name
 * - componentName: Name of the component to render ('CheckboxField' or 'BoosterField')
 * - icon: Icon name for the component
 * - titleKey: Translation key for the title
 * - infoKey: Translation key for additional info (optional)
 * - checkPrice: Whether to check for pricing (optional)
 * - customLogic: Function to handle custom availability/display logic (optional)
 * - customProps: Function to generate additional props based on context (optional)
 */
export const VEHICLE_OPTIONS_CONFIG = [
  {
    key: 'pet',
    formField: 'pet',
    componentName: 'CheckboxField',
    icon: 'Pet',
    titleKey: 'title-pet-option',
    tabIndex: 0,
  },
  {
    key: 'low',
    formField: 'low',
    componentName: 'CheckboxField',
    icon: 'Low',
    titleKey: 'title-low-option',
    infoKey: 'info-low-option',
    tabIndex: 0,
  },
  /*{
    key: 'stationWagon',
    formField: 'stationWagon',
    componentName: 'CheckboxField',
    icon: 'CarEstate',
    titleKey: 'title-stationwagon-option',
    tabIndex: 0,
  },*/
  {
    key: 'boosterSeat',
    // For booster seats, we check both boosterSeat1 and boosterSeat2
    apiKeys: ['boosterSeat1', 'boosterSeat2'],
    formField: 'booster',
    componentName: 'BoosterField',
    icon: 'BoosterSeat',
    titleKey: 'title-booster-option',
    infoKey: 'info-booster-option',
    tabIndex: 0,
    checkPrice: true,
    // Custom logic for booster seats (different from standard options)
    customLogic: (vehicleOptions, editableVehicleOptions, values, inEditMode) => {
      const haveRearFacingChildSeat = values.rearFacingChildSeat;

      const boosterSeat1Available = vehicleOptions.includes('boosterSeat1')
      const boosterSeat2Available = vehicleOptions.includes('boosterSeat2') && !haveRearFacingChildSeat;
      const currentlySelected = values.booster > 0
      
      const boosterSeat1IsEditable = editableVehicleOptions.includes('boosterSeat1')
      const boosterSeat2IsEditable = editableVehicleOptions.includes('boosterSeat2') && !haveRearFacingChildSeat;

      const available = boosterSeat1Available || boosterSeat2Available
      const isEditable = boosterSeat1IsEditable || boosterSeat2IsEditable
      const show = available || (inEditMode && currentlySelected)

      const numSeatsMax = boosterSeat1Available || (inEditMode && boosterSeat1IsEditable)
        ? (boosterSeat2Available || (inEditMode && boosterSeat2IsEditable) ? 2 : 1)
        : 0

      return {
        available,
        currentlySelected,
        isEditable,
        show,
        numSeatsMax,
      }
    },
    customProps: (context) => ({
      numSeatsMax: context.numSeatsMax,
    }),
    shouldShow: (values) => values?.serviceType !== 'business',
  },
  {
    key: 'rearFacingChildSeat',
    formField: 'rearFacingChildSeat',
    componentName: 'CheckboxField',
    icon: 'ChildSeat',
    titleKey: 'title-childseat-option',
    infoKey: 'info-childseat-option',
    tabIndex: 2,
    checkPrice: true,
  },
]

/**
 * Process a vehicle option to determine its state
 * @param {object} option - The option config from VEHICLE_OPTIONS_CONFIG
 * @param {array} vehicleOptions - Available vehicle options from API
 * @param {array} editableVehicleOptions - Editable options in edit mode
 * @param {object} values - Formik values
 * @param {boolean} inEditMode - Whether in edit mode
 * @returns {object} Processed option state
 */
export const processVehicleOption = (option, vehicleOptions, editableVehicleOptions, values, inEditMode) => {
  // If custom logic is provided, use it
  if (option.customLogic) {
    return {
      ...option,
      ...option.customLogic(vehicleOptions, editableVehicleOptions, values, inEditMode),
    }
  }

  // Standard logic for simple options
  const apiKey = option.apiKeys ? option.apiKeys[0] : option.key
  const available = option.apiKeys
    ? option.apiKeys.some(key => vehicleOptions.includes(key))
    : vehicleOptions.includes(apiKey)

  const currentlySelected = values[option.formField] === true

  const isEditable = option.apiKeys
    ? option.apiKeys.some(key => editableVehicleOptions.includes(key))
    : editableVehicleOptions.includes(apiKey)

  const show = available || (inEditMode && currentlySelected)

  return {
    ...option,
    available,
    currentlySelected,
    isEditable,
    show,
  }
}
